import { ACHIEVEMENTS, AchievementDef } from '../../engine/Achievements'
import Icon from './Icon'

interface AchievementPanelProps {
  unlockedMap: Record<string, number>   // id -> unlockedAt; 缺失 = 未解锁
  compact?: boolean                      // 紧凑模式 (用在首页)
  hideHeader?: boolean                    // 不渲染标题 (外部已渲染)
}

/**
 * 勋章面板 - 像素风 6x2 网格
 * 暗色/明亮模式都用同一组件, 颜色靠 CSS 变量自动适配
 */
function AchievementPanel({ unlockedMap, compact = false, hideHeader = false }: AchievementPanelProps) {
  const unlockedCount = Object.keys(unlockedMap).filter(k => (unlockedMap[k] ?? 0) > 0).length
  const total = ACHIEVEMENTS.length

  return (
    <div className="pixel-box p-3 mb-4">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-pixel text-[10px] text-[color:var(--color-gold)] tracking-wider">
            ► 勋章成就
          </h3>
          <span className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
            {unlockedCount} / {total}
          </span>
        </div>
      )}

      <div
        className={
          compact
            ? 'grid grid-cols-5 gap-2'
            : 'grid grid-cols-3 sm:grid-cols-5 gap-2'
        }
      >
        {ACHIEVEMENTS.map((ach) => {
          const unlockedAt = unlockedMap[ach.id]
          const unlocked = !!unlockedAt && unlockedAt > 0
          return (
            <AchievementBadge
              key={ach.id}
              achievement={ach}
              unlocked={unlocked}
              compact={compact}
            />
          )
        })}
      </div>
    </div>
  )
}

function AchievementBadge({
  achievement,
  unlocked,
  compact,
}: {
  achievement: AchievementDef
  unlocked: boolean
  compact?: boolean
}) {
  return (
    <div
      className={
        'flex flex-col items-center text-center p-2 pixel-box ' +
        (unlocked
          ? 'bg-[color:var(--color-bg-elevated)] border-[color:var(--color-gold)]'
          : 'bg-[color:var(--color-bg-base)] border-[color:var(--color-border)]')
      }
      title={unlocked ? `${achievement.name}: ${achievement.description}` : `未解锁 · ${achievement.name}: ${achievement.description}`}
    >
      <div className="relative w-14 h-14 mb-1 flex items-center justify-center">
        <img
          src={achievement.imagePath}
          alt={achievement.name}
          width={56}
          height={56}
          className={
            'object-contain transition-all ' +
            (unlocked ? '' : 'grayscale opacity-50')
          }
          style={{ imageRendering: 'auto' }}
        />
        {!unlocked && (
          <span
            className="absolute inset-0 flex items-center justify-center pointer-events-none text-[color:var(--color-text-dim)]"
            style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}
          >
            <Icon name="lock" size={20} />
          </span>
        )}
      </div>
      {!compact && (
        <div
          className={
            'font-pixel text-[9px] leading-tight ' +
            (unlocked
              ? 'text-[color:var(--color-text-bright)]'
              : 'text-[color:var(--color-text-dim)]')
          }
        >
          {achievement.name}
        </div>
      )}
    </div>
  )
}

export default AchievementPanel